import React from 'react';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import NavigationBarComponent from 'components/NavigationBar';
import { NavigationLink, StyledNavElement } from 'components/NavigationBar/styles';
import { useFormConfig } from 'config/formConfig/hooks';
import paths from 'constants/paths';
import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import ArrowLeftIcon from 'theme/components/Icon/ArrowLeftIcon';
import { useNavigationAriaCurrent } from 'utils/hooks/useNavigationAriaCurrent';

import {
  NAVIGATION_DASHBOARD_LINK,
  NAVIGATION_DETAILS_LINK,
  NAVIGATION_DOCUMENTS_LINK,
  NAVIGATION_BACK_BUTTON,
} from '../../../../modules/Inquiry/Form/formFields';

const NavigationLinkElement = ({ children, ...rest }) => {
  const ariaCurrent = useNavigationAriaCurrent(rest.to);
  return (
    <StyledNavElement>
      <NavigationLink {...rest} aria-current={ariaCurrent}>
        {children}
      </NavigationLink>
    </StyledNavElement>
  );
};

const NavigationBar = ({ id }) => {
  const { formatMessage } = useIntl();
  const { selectedInquiryType } = useFormConfig();

  const dashboardNavigation = [
    <NavigationLinkElement
      key="dashboard"
      to={paths.customer.inquiryDetails.dashboard.replace(':id', id)}
      data-testid={NAVIGATION_DASHBOARD_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.dashboard' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="documentExchange"
      to={paths.customer.inquiryDetails.documentExchange.replace(':id', id)}
      data-testid={NAVIGATION_DOCUMENTS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.documentExchange' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="details"
      to={paths.customer.inquiryDetails.details.replace(':id', id)}
      data-testid={NAVIGATION_DETAILS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.details' })}
    </NavigationLinkElement>,
  ];

  const leaseplanNavigation = [
    <NavigationLinkElement
      key="dashboard"
      to={paths.customer.inquiryDetails.dashboard.replace(':id', id)}
      data-testid={NAVIGATION_DASHBOARD_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.dashboard' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="documentExchange"
      to={paths.customer.inquiryDetails.documentExchange.replace(':id', id)}
      data-testid={NAVIGATION_DOCUMENTS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.documentExchange' })}
    </NavigationLinkElement>,
  ];

  const mainNavigation = [
    <NavigationLinkElement
      key="dashboard"
      to={paths.customer.inquiryDetails.dashboard.replace(':id', id)}
      data-testid={NAVIGATION_DASHBOARD_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.dashboard' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="details"
      to={paths.customer.inquiryDetails.details.replace(':id', id)}
      data-testid={NAVIGATION_DETAILS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.details' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="documentExchange"
      to={paths.customer.inquiryDetails.documentExchange.replace(':id', id)}
      data-testid={NAVIGATION_DOCUMENTS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.documentExchange' })}
    </NavigationLinkElement>,
  ];

  const bfsServiceNavigation = [
    <NavigationLinkElement
      key="dashboard"
      to={paths.customer.inquiryDetails.dashboard.replace(':id', id)}
      data-testid={NAVIGATION_DASHBOARD_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.dashboard' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="documentExchange"
      to={paths.customer.inquiryDetails.documentExchange.replace(':id', id)}
      data-testid={NAVIGATION_DOCUMENTS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.documentExchange' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="details"
      to={paths.customer.inquiryDetails.details.replace(':id', id)}
      data-testid={NAVIGATION_DETAILS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.details' })}
    </NavigationLinkElement>,
  ];

  const cbBankNavigation = [
    <NavigationLinkElement
      key="dashboard"
      to={paths.customer.inquiryDetails.dashboard.replace(':id', id)}
      data-testid={NAVIGATION_DASHBOARD_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.dashboard' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="documentExchange"
      to={paths.customer.inquiryDetails.documentExchange.replace(':id', id)}
      data-testid={NAVIGATION_DOCUMENTS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.documentExchange' })}
    </NavigationLinkElement>,
    <NavigationLinkElement
      key="details"
      to={paths.customer.inquiryDetails.details.replace(':id', id)}
      data-testid={NAVIGATION_DETAILS_LINK}
    >
      {formatMessage({ id: 'pages.inquiryDetails.navigation.details' })}
    </NavigationLinkElement>,
  ];

  const inquiryTypeNavigation = {
    [InquiryType.onlinefactoring]: dashboardNavigation,
    [InquiryType.leaseplan]: leaseplanNavigation,
    [InquiryType.bfsService]: bfsServiceNavigation,
    [InquiryType.cbBank]: cbBankNavigation,
    default: mainNavigation,
  };

  const additionalNavigation = (
    <NavigationLinkElement
      key="back"
      exact
      to={paths.customer.inquiryList}
      data-testid={NAVIGATION_BACK_BUTTON}
    >
      <ArrowLeftIcon boxSize={6} mr={2} />
      {formatMessage({ id: 'buttons.back' })}
    </NavigationLinkElement>
  );

  return (
    <NavigationBarComponent
      mainNavigation={inquiryTypeNavigation[selectedInquiryType] || inquiryTypeNavigation.default}
      additionalNavigation={additionalNavigation}
    />
  );
};

NavigationBar.propTypes = {
  id: PropTypes.string.isRequired,
};

export default NavigationBar;
